/**
 * One StandardMaterial3D → one three material description: the class and its prop bag.
 * `build.ts` and `<StandardMaterialSlot>` read only this, so both mean the same thing.
 * Each adapter binds textures first (`textureBinding.ts`). It value-imports `three`, so `index.ts`
 * never imports it.
 */
import * as THREE from 'three';
import { GODOT_DEFAULT_ALBEDO, GODOT_DEFAULT_LIGHT_MODES } from '../../../r3f/materials/godotDefaultMaterial';
import { DEFAULT_MATERIAL_METALLIC, DEFAULT_MATERIAL_ROUGHNESS, } from '../../../godot/rendering/materials/defaultMaterial';
import { BillboardMode } from '../../../godot/rendering/materials/billboardMode';
import { BASE_MATERIAL_3D_DEFAULTS } from '../../../godot/scene/baseMaterial3D';
import { surfaceFadeVariants } from '../../../r3f/materials/fadeVariants';
import { composeInjections } from '../../../r3f/materialProgramInputs';
import { SRGB_VERTEX_COLOR } from '../../../r3f/materials/godotVertexColor';
import { alphaHashScaleUserData } from '../../../r3f/materials/godotAlphaHash';
import { viewportSamplersUserData } from '../../../r3f/materials/godotMipmapBias';
import { alphaAntialiasingProps, alphaAntialiasingUserData, } from '../../../r3f/materials/godotAlphaAntialiasing';
import { recordedOn } from '../../../r3f/materials/recordedOnMaterial';
import { emissionTextureAddInjection, emissionTextureAddUserData, } from '../../../r3f/materials/emissionTextureAdd';
import { threeAnisotropy } from '../../../r3f/godotLightFunction';
import { lightModesInjection } from '../../../r3f/lightModes';
import { GODOT_NORMAL_MAP_READ } from '../../../r3f/materials/godotNormalMap';
import { billboardInjections } from '../../../r3f/surfaceBillboard';
import { growInjectionOf } from '../../../r3f/materials/godotGrow';
import { withDepthInjection } from '../../../r3f/materials/depthInjections';
import { SPECULAR_OCCLUSION_DISABLED } from '../../../r3f/godotIndirectSpecular';
import { heightmapUserData, parallaxInjection, unshadedParallaxInjection, } from '../../../r3f/materials/godotParallax';
import { GODOT_METALLIC_ROUGHNESS_CHANNELS, metallicRoughnessChannelsUserData, } from '../../../r3f/materials/godotMetallicRoughnessChannels';
import { GODOT_AMBIENT_OCCLUSION, ambientOcclusionUserData, } from '../../../r3f/materials/godotAmbientOcclusion';
import { triplanarUserData, } from '../../../r3f/materials/godotTriplanar';
import { DRAWN_OPAQUE_PREPASS, FADED_OPAQUE_PREPASS, NO_OPAQUE_PREPASS, opaquePrepassUserData, } from '../../../r3f/materials/opaquePrepass';
import { resolveEmission } from './emission';
import { DEFAULT_ROUGHNESS, UNSHADED_ROUGHNESS, unshadedRoughnessUserData, } from '../../../r3f/screenSpaceEffects/unshadedRoughness';
import { TEXTURE_SLOTS, } from './types';
// Godot's default COLOR buffer is white (`mesh_storage.cpp:86-97`). three declares it on
// ShaderMaterial alone, so another class reads (0,0,0) and blacks out albedo. On the
// prototype, as `copy` drops it, and here, as this module mints every material that can
// read the default.
THREE.Material.prototype.defaultAttributeValues = {
    color: [1, 1, 1],
};
/**
 * Godot's default 3D material, which a surface with no material draws. A hardcoded
 * shader, not a default StandardMaterial3D, so it has no scalars (`godotDefaultMaterial.ts`).
 * A fresh bag per call: three and R3F assign `userData` by reference, so one module-level
 * object would carry a key one surface's material writes onto every default surface.
 */
function noMaterialBag() {
    return {
        materialClass: 'standard',
        props: {
            color: GODOT_DEFAULT_ALBEDO,
            metalness: DEFAULT_MATERIAL_METALLIC,
            roughness: DEFAULT_MATERIAL_ROUGHNESS,
            side: THREE.FrontSide,
            userData: viewportSamplersUserData(),
        },
        injection: lightModesInjection(GODOT_DEFAULT_LIGHT_MODES),
    };
}
/**
 * Godot's default surface writes no ALPHA, and its three bag binds no map and no vertex colours, so
 * three's alpha is `opacity` alone too: no patch, as for a shader that reads the albedo alpha. It
 * writes depth in the opaque pass only.
 */
const NO_MATERIAL_ALPHA = { readsAlbedoAlpha: true, opaqueAfterCut: false, alphaPassDepthWrite: false };
/**
 * The previewer's marker for a surface whose texture never draws: a file that cannot load,
 * or a ViewportTexture albedo whose pass is cyclic. Godot has no such material.
 */
export const MISSING_TEXTURE_MATERIAL = {
    materialClass: 'standard',
    props: { color: 'magenta' },
};
/**
 * `MeshPhysicalMaterial` is needed for clearcoat, rim → sheen, anisotropy or refraction →
 * transmission. A three capability mapping, as Godot has one spatial shader, so a new
 * physical-only feature extends this set.
 */
function needsPhysicalMaterial(scalars) {
    return (scalars.clearcoat > 0 ||
        scalars.rim > 0 ||
        isAnisotropic(scalars) ||
        scalars.transmission > 0 ||
        // The default 0.5 maps to three's fixed F0 of 0.04, so only another value needs `specularIntensity`.
        scalars.specular !== BASE_MATERIAL_3D_DEFAULTS.specular);
}
/**
 * three's `specularIntensity` for Godot's dielectric F0 of `0.16 * specular * specular`
 * (`scene_forward_lights_inc.glsl:56`): three scales its F0 of 0.04 at ior 1.5.
 */
function specularIntensity(specular) {
    return (0.16 * specular * specular) / 0.04;
}
/** The sRGB decode of a surface whose albedo reads an sRGB vertex colour, else none. */
function vertexColorInjection(scalars) {
    return scalars.useVertexColors && scalars.vertexColorIsSrgb ? SRGB_VERTEX_COLOR : undefined;
}
/**
 * Whether the surface draws anisotropic, and so samples `anisotropyMap`. `anisotropy` is signed,
 * and either sign scales by the flowmap's strength (`material.cpp:1932`).
 */
export function isAnisotropic(scalars) {
    return scalars.anisotropy !== 0;
}
/**
 * `rim_tint` blends the rim highlight from the light colour (0) toward the
 * albedo (1); three's closest native term is `sheenColor`.
 */
function rimSheenColor(scalars) {
    const tint = scalars.rimTint;
    return new THREE.Color(1 + tint * (scalars.color[0] - 1), 1 + tint * (scalars.color[1] - 1), 1 + tint * (scalars.color[2] - 1));
}
/**
 * A low `sheenRoughness` concentrates the sheen toward grazing angles, so the
 * effect reads as an edge rim rather than a broad fabric glow that would wash a
 * dark-albedo sphere out to bright grey.
 */
const RIM_SHEEN_ROUGHNESS = 0.1;
/**
 * The blending fields, with the factor fields omitted where a three preset carries them.
 * Omitted, not `undefined`: `Material.setValues` warns on one, and R3F would assign it
 * over three's own blending state.
 */
function materialBlendProps(scalars) {
    const props = { blending: scalars.blending };
    if (scalars.blendEquation !== undefined)
        props.blendEquation = scalars.blendEquation;
    if (scalars.blendSrc !== undefined)
        props.blendSrc = scalars.blendSrc;
    if (scalars.blendDst !== undefined)
        props.blendDst = scalars.blendDst;
    if (scalars.blendEquationAlpha !== undefined) {
        props.blendEquationAlpha = scalars.blendEquationAlpha;
    }
    if (scalars.blendSrcAlpha !== undefined)
        props.blendSrcAlpha = scalars.blendSrcAlpha;
    if (scalars.blendDstAlpha !== undefined)
        props.blendDstAlpha = scalars.blendDstAlpha;
    return props;
}
/** Where a derived material records the surface state the draw hooks read per draw group. */
const BILLBOARD_KEY = 'godotBillboard';
const CASTS_SHADOW_KEY = 'godotCastsShadow';
/**
 * Surface state on `userData`, not as material props: the draw hooks apply billboard, shadow and
 * hash scale per draw group (`r3f/surfaceDrawHooks.ts`), the AO, height and edge-ramp uniforms read
 * theirs at upload. A fresh object per bag, as the `.tres` loader writes keys into it.
 */
function surfaceUserData(scalars) {
    const billboard = surfaceBillboard(scalars);
    const userData = {
        [BILLBOARD_KEY]: billboard,
        [CASTS_SHADOW_KEY]: scalars.castsShadow,
        ...alphaHashScaleUserData(scalars.alphaHashScale),
        ...alphaAntialiasingUserData(scalars.alphaAntialiasing),
        ...ambientOcclusionUserData(scalars.aoLightAffect, scalars.aoTextureChannelMask),
        ...metallicRoughnessChannelsUserData(scalars.roughnessTextureChannelMask, scalars.metallicTextureChannelMask),
        ...heightmapUserData(scalars.heightmap),
        ...triplanarUserData(triplanarRecord(scalars)),
        ...viewportSamplersUserData(),
    };
    // A billboard's shadow billboards as its colour pass does (`material.cpp:1309`).
    const injections = billboardInjections(billboard);
    return injections ? withDepthInjection(userData, 'afterCut', injections.depth) : userData;
}
/**
 * three's map that draws each slot. The heightmap has the parallax's own sampler, and Godot never
 * projects it: triplanar mapping turns height mapping off (`material.cpp:1541`).
 */
const SLOT_MAPS = {
    albedo_texture: 'map',
    normal_texture: 'normalMap',
    roughness_texture: 'roughnessMap',
    metallic_texture: 'metalnessMap',
    emission_texture: 'emissiveMap',
    ao_texture: 'aoMap',
    heightmap_texture: null,
    anisotropy_flowmap: 'anisotropyMap',
};
/**
 * The triplanar mapping `scalars` draw with, each UV set with the maps that read it, or null for
 * none. Under `use_point_size` the albedo reads POINT_COORD, not a projection (`material.cpp:1612-1620`).
 */
function triplanarRecord(scalars) {
    const { triplanar } = scalars;
    if (!triplanar)
        return null;
    const mapsOf = (slots) => slots.flatMap((slot) => SLOT_MAPS[slot] ?? []);
    const uv1Slots = TEXTURE_SLOTS.filter((slot) => !scalars.uv2Slots.includes(slot) && !(slot === 'albedo_texture' && scalars.usesPointSize));
    return {
        ...triplanar,
        isBillboard: billboards(scalars),
        uv1Maps: triplanar.uv1 ? mapsOf(uv1Slots) : [],
        uv2Maps: triplanar.uv2 ? mapsOf(scalars.uv2Slots) : [],
    };
}
/** The billboard of Godot's default surface and of any material this derivation did not build. */
const NO_BILLBOARD = Object.freeze({
    mode: BillboardMode.BILLBOARD_DISABLED,
    keepScale: false,
});
/**
 * The modes `_update_shader` writes a billboard for (`material.cpp:1260-1335`). Any other
 * value draws as DISABLED, so it must not unbatch a GridMap item either.
 */
const BILLBOARDING_MODES = new Set([
    BillboardMode.BILLBOARD_ENABLED,
    BillboardMode.BILLBOARD_FIXED_Y,
    BillboardMode.BILLBOARD_PARTICLES,
]);
/** Whether `scalars` describe a surface that billboards. */
function billboards(scalars) {
    return scalars !== null && BILLBOARDING_MODES.has(scalars.billboardMode);
}
/**
 * The billboard `scalars` describe, null being Godot's default surface. Built once per
 * bag, so the draw hooks read it every draw group without allocating.
 */
export function surfaceBillboard(scalars) {
    if (!billboards(scalars))
        return NO_BILLBOARD;
    return { mode: scalars.billboardMode, keepScale: scalars.billboardKeepScale };
}
/**
 * Godot's billboard for a surface that billboards: the pose and the normal turn, after any
 * patch that adds a `defaultnormal_vertex`.
 */
function billboardInjection(scalars) {
    return billboardInjections(surfaceBillboard(scalars))?.colour;
}
/** The billboard a material was derived with. */
export function billboardOf(material) {
    return recordedOn(material, BILLBOARD_KEY, NO_BILLBOARD);
}
/**
 * Whether a material's surface joins Godot's shadow pass. True for Godot's default
 * surface, which is opaque, and for any material this derivation did not build.
 */
export function castsShadowOf(material) {
    return recordedOn(material, CASTS_SHADOW_KEY, true);
}
/**
 * Derive the material this decoded StandardMaterial3D describes, in each pass a geometry
 * instance's fade can draw it in: unfaded, and the alpha pass.
 *
 * @param scalars - the decoded material, or null for a surface with none
 * @param textures - already-bound textures by Godot slot; an absent slot lands
 *   as `null`, never `undefined`, so a late arrival cannot be mistaken for
 *   "leave whatever the material has" by either adapter
 */
export function standardMaterialBags(scalars, textures = {}) {
    const bag = scalars ? classBag(scalars, textures) : noMaterialBag();
    const source = scalars ?? NO_MATERIAL_ALPHA;
    const surface = {
        opacity: bag.props.opacity ?? 1,
        transparent: bag.props.transparent ?? false,
        depthWrite: bag.props.depthWrite ?? true,
        alphaPassDepthWrite: source.alphaPassDepthWrite,
        blending: bag.props.blending,
    };
    const { unfaded, alphaPass } = surfaceFadeVariants(source, surface);
    const prepass = scalars?.depthInAlphaPass === true;
    return {
        unfaded: withSurfaceAlpha(bag, unfaded, prepass ? DRAWN_OPAQUE_PREPASS : NO_OPAQUE_PREPASS),
        alphaPass: withSurfaceAlpha(bag, alphaPass, prepass ? FADED_OPAQUE_PREPASS : NO_OPAQUE_PREPASS),
    };
}
function withSurfaceAlpha(bag, { injection, ...alpha }, prepass) {
    const props = { ...bag.props, ...alpha };
    if (prepass.cutsDepth)
        props.userData = { ...bag.props.userData, ...opaquePrepassUserData(prepass) };
    return { ...bag, props, injection: composeInjections([bag.injection, injection]) };
}
/** The class `scalars` need, and its props. */
function classBag(scalars, textures) {
    const slot = (name) => textures[name] ?? null;
    const userData = surfaceUserData(scalars);
    const { heightmap } = scalars;
    const heightmapTexture = heightmap ? slot('heightmap_texture') : null;
    // Alpha antialiasing replaces the material's own blend mode (`scene_shader_forward_clustered.cpp:237-241`).
    const blend = { ...materialBlendProps(scalars), ...alphaAntialiasingProps(scalars.alphaAntialiasing) };
    // `fromArray` keeps the values linear. A hex would go through
    // `setHex(…, SRGBColorSpace)` and decode these already-linear channels a
    // second time, rendering everything too dark.
    const color = new THREE.Color().fromArray(scalars.color);
    // Godot SHADING_MODE_UNSHADED (0) outputs albedo unlit, as three's MeshBasicMaterial
    // does, and still shifts it by the heightmap. Dropping emission is parity: Godot's
    // unshaded branch writes `frag_color = vec4(albedo, alpha)` and never reads the emission term.
    // Its roughness reaches only the normal-roughness prepass (`unshadedRoughness.ts`).
    if (scalars.shadingMode === 'unshaded') {
        const roughnessMap = slot('roughness_texture');
        return {
            materialClass: 'basic',
            props: {
                color,
                vertexColors: scalars.useVertexColors,
                map: slot('albedo_texture'),
                specularMap: roughnessMap,
                transparent: scalars.transparent,
                opacity: scalars.opacity,
                alphaTest: scalars.alphaTest,
                alphaHash: scalars.alphaHash,
                depthWrite: scalars.depthWrite,
                depthTest: scalars.depthTest,
                side: scalars.side,
                userData: { ...userData, ...unshadedRoughnessUserData(scalars.roughness) },
                ...blend,
            },
            injection: composeInjections([
                roughnessMap || scalars.roughness !== DEFAULT_ROUGHNESS ? UNSHADED_ROUGHNESS : undefined,
                vertexColorInjection(scalars),
                heightmap ? unshadedParallaxInjection(heightmap) : undefined,
                billboardInjection(scalars),
                growInjectionOf(scalars),
            ]),
            heightmapTexture,
        };
    }
    const emissiveMap = slot('emission_texture');
    const aoMap = slot('ao_texture');
    // Godot's `emission_operator` only becomes observable once a texture is in
    // play, and whether one resolved is knowable here and not at decode time.
    const emission = resolveEmission(scalars, scalars.emissionOperator, !!emissiveMap);
    const pbr = {
        color,
        vertexColors: scalars.useVertexColors,
        metalness: scalars.metalness,
        roughness: scalars.roughness,
        transparent: scalars.transparent,
        opacity: scalars.opacity,
        alphaTest: scalars.alphaTest,
        alphaHash: scalars.alphaHash,
        depthWrite: scalars.depthWrite,
        depthTest: scalars.depthTest,
        side: scalars.side,
        map: slot('albedo_texture'),
        normalMap: slot('normal_texture'),
        normalScale: new THREE.Vector2(scalars.normalScale.x, scalars.normalScale.y),
        roughnessMap: slot('roughness_texture'),
        metalnessMap: slot('metallic_texture'),
        emissiveMap,
        aoMap,
        // Field by field, not spread: `scalars` is far wider than `EmissionScalars`, and a
        // pass-through in `resolveEmission` would splat every scalar onto the material.
        emissive: new THREE.Color().fromArray(emission.emissive),
        emissiveIntensity: emission.emissiveIntensity,
        userData: { ...userData, ...emissionTextureAddUserData(emission.addedTextureEnergy) },
        ...blend,
    };
    // Godot's AO sampler is `hint_default_white` (`material.cpp:1128`), so a surface with no map
    // occludes nothing itself. A decal's ORM map can still occlude it, in the direct light too once
    // `ao_light_affect` is set.
    const injection = composeInjections([
        lightModesInjection(scalars),
        vertexColorInjection(scalars),
        aoMap || scalars.aoLightAffect > 0 ? GODOT_AMBIENT_OCCLUSION : undefined,
        pbr.roughnessMap || pbr.metalnessMap ? GODOT_METALLIC_ROUGHNESS_CHANNELS : undefined,
        pbr.normalMap ? GODOT_NORMAL_MAP_READ : undefined,
        scalars.disablesSpecularOcclusion ? SPECULAR_OCCLUSION_DISABLED : undefined,
        heightmap ? parallaxInjection(heightmap) : undefined,
        emissionTextureAddInjection(emission.addedTextureEnergy),
        billboardInjection(scalars),
        growInjectionOf(scalars),
    ]);
    if (!needsPhysicalMaterial(scalars)) {
        return { materialClass: 'standard', props: pbr, injection, heightmapTexture };
    }
    return {
        materialClass: 'physical',
        injection,
        heightmapTexture,
        props: {
            ...pbr,
            specularIntensity: specularIntensity(scalars.specular),
            clearcoat: scalars.clearcoat,
            clearcoatRoughness: scalars.clearcoatRoughness,
            sheen: scalars.rim,
            sheenColor: rimSheenColor(scalars),
            sheenRoughness: RIM_SHEEN_ROUGHNESS,
            ...threeAnisotropy(scalars.anisotropy),
            // The flowmap needs an alpha→blue repack before it means anything, so
            // only an adapter that can do one ever fills this slot.
            anisotropyMap: slot('anisotropy_flowmap'),
            transmission: scalars.transmission,
            thickness: scalars.refractionThickness,
        },
    };
}
//# sourceMappingURL=materialBag.js.map